/**
 * Picking two teams and their starting elevens out of the converted database.
 *
 * The original reads all this from a SQLite file at match start (`TeamData::TeamData`,
 * teamdata.cpp:60-262). The conversion has already flattened it to JSON — 8 teams, 144
 * players — so what is left here is the selection rules, not the parsing.
 *
 * THE RULES, such as they are:
 *   * A team's squad is ordered by a stored "formation order" column, and the first eleven
 *     are the ones who play (teamdata.cpp:247-256, team.cpp:78-95). There is no lineup
 *     screen: the menu page that would edit this is disabled in a release build
 *     (docs/original-engine/shell-and-data.md).
 *   * Squad position N takes formation slot N. That is the whole mapping.
 *   * The home side wears its first kit and the away side its second, always
 *     (menutask.hpp:39-40). Keepers wear the one goalkeeper kit that ships with the game,
 *     shared by both teams (team.cpp:91).
 */

import type { FormationEntry, PlayerRole } from './formation';
import { buildFormation } from './formation';
import type { PlayerAppearance } from '../render/humanoid';

/** The shape of public/assets/data/database.json, narrowed to what a match needs. */
export interface DatabaseFile {
  teams: DatabaseTeam[];
  players: DatabasePlayer[];
}

export interface DatabaseTeam {
  id: number;
  name: string;
  shortName: string;
  /** e.g. "images_teams/eredivisie/afcajax" — kit filenames are built from it. */
  kitUrlRaw: string;
  formation: { index: number; role: string; position: [number, number] }[];
}

export interface DatabasePlayer {
  id: number;
  teamId: number;
  firstName: string;
  lastName: string;
  age: number;
  height: number;
  skinColor: number;
  hairStyle: string;
  hairColor: string;
  formationOrder: number;
  /**
   * The 22 attributes already combined with how good this player is overall and with the
   * age curve, each 0..1. The separate `profile` block is the raw shape before that
   * combination and is not what the game reads.
   */
  stats: Record<string, number>;
}

export interface LineupPlayer {
  databaseId: number;
  name: string;
  /** 0..10, the formation slot this player fills. */
  slot: number;
  role: PlayerRole;
  appearance: PlayerAppearance;
  /** How fast, how agile, how balanced. Drives the animation physics solver. */
  stats: Readonly<Record<string, number>>;
}

export interface TeamLineup {
  teamId: number;
  name: string;
  shortName: string;
  /** 0 = the side that starts defending the negative-x goal. */
  index: 0 | 1;
  formation: FormationEntry[];
  players: LineupPlayer[];
}

export const GOALKEEPER_KIT = 'media/objects/players/textures/goalie_kit.png';
/** The original falls back to these when a team has no kit art (team.cpp:89). */
const FALLBACK_KIT = ['media/textures/almost_white.png', 'media/textures/almost_black.png'];

/**
 * Which shirt image the outfield players of one side wear.
 *
 * Split out so the visual-options page can swap a side into its other kit in the middle of a
 * match without repeating the naming rule or the plain-white / plain-black fallback.
 *
 * @param kitNumber 1 or 2. Each team ships two; the home side opens in its first and the away
 *                  side in its second (`menutask.hpp:39-40`).
 */
export function outfieldKitTexture(
  team: DatabaseTeam,
  index: 0 | 1,
  kitNumber: number,
  hasTexture: (key: string) => boolean,
): string {
  const key = `databases/default/${team.kitUrlRaw}_kit_0${kitNumber}.png`;
  return hasTexture(key) ? key : FALLBACK_KIT[index]!;
}

/**
 * Roles as the database spells them. Anything unrecognised becomes a centre midfielder,
 * which is what the original's string-to-role lookup does with an unknown name.
 */
function toRole(text: string): PlayerRole {
  const known: PlayerRole[] = ['GK', 'CB', 'LB', 'RB', 'DM', 'CM', 'LM', 'RM', 'AM', 'CF'];
  const found = known.find((role) => role === text.toUpperCase());
  return found ?? 'CM';
}

/**
 * Build one team's line-up.
 *
 * @param index 0 for the home side, 1 for the away side. Decides which kit is worn and,
 *              through `Team::GetSide`, which goal is defended.
 * @param hasTexture tells us whether a kit image actually exists, so the original's
 *              plain-white / plain-black fallback can be reproduced rather than guessed at.
 */
export function buildLineup(
  database: DatabaseFile,
  team: DatabaseTeam,
  index: 0 | 1,
  hasTexture: (key: string) => boolean,
): TeamLineup {
  const formation = buildFormation(
    team.formation.map((slot) => ({ role: toRole(slot.role), position: slot.position })),
  );

  const outfieldKit = outfieldKitTexture(team, index, index === 0 ? 1 : 2, hasTexture);

  const squad = database.players
    .filter((player) => player.teamId === team.id)
    .sort((a, b) => a.formationOrder - b.formationOrder);

  const players: LineupPlayer[] = squad.slice(0, formation.length).map((player, slot) => {
    const role = formation[slot]!.role;
    return {
      databaseId: player.id,
      name: `${player.firstName} ${player.lastName}`.trim(),
      slot,
      role,
      appearance: {
        height: player.height,
        skinColor: player.skinColor,
        hairStyle: player.hairStyle,
        hairColor: player.hairColor,
        kitTexture: role === 'GK' ? GOALKEEPER_KIT : outfieldKit,
      },
      stats: player.stats ?? {},
    };
  });

  if (players.length < formation.length) {
    throw new Error(
      `${team.name} has only ${players.length} players for ${formation.length} formation slots`,
    );
  }

  return {
    teamId: team.id,
    name: team.name,
    shortName: team.shortName,
    index,
    formation,
    players,
  };
}

/**
 * The two teams for a friendly.
 *
 * The original would take these from the menu's queued fixture. There is no menu yet, so
 * the first two teams in the database play — deterministic, and easy to change once there
 * is something to change it from.
 */
export function defaultFixture(database: DatabaseFile): [DatabaseTeam, DatabaseTeam] {
  const [home, away] = database.teams;
  if (!home || !away) throw new Error('The database has fewer than two teams');
  return [home, away];
}
